import { X } from "lucide-react";
import type { CSSProperties, JSX, ReactNode } from "react";
import { cx } from "../cx";

export interface FilterChipProps {
  /** Filter dimension or expression ("Kinds", "capability = blob.put"). */
  label: string;
  /** Applied count, rendered in mono. */
  count?: number | string | undefined;
  /** Applied filter — strong border; announced through aria-pressed. */
  active?: boolean | undefined;
  onToggle?: (() => void) | undefined;
  /** Renders the clear button ("Remove <label>"). */
  removable?: boolean | undefined;
  onRemove?: (() => void) | undefined;
  /** Leading lucide icon at 16px, aria-hidden. */
  icon?: ReactNode | undefined;
  className?: string | undefined;
  style?: CSSProperties | undefined;
}

/**
 * Toggleable toolbar filter chip. Height tracks --control-h so it densifies
 * under data-density="compact"; the applied state gets the strong border.
 * The chip is a toggle button (aria-pressed); `removable` adds a separate
 * clear button so both actions stay keyboard-reachable. Ported from
 * design/tarski-kit/components/studio/FilterChip.jsx.
 */
export function FilterChip({
  label,
  count,
  active = false,
  onToggle,
  removable = false,
  onRemove,
  icon,
  className,
  style,
}: FilterChipProps): JSX.Element {
  return (
    <span
      className={cx(
        "tk-filter-chip",
        active && "tk-filter-chip--active",
        removable && "tk-filter-chip--removable",
        className,
      )}
      style={style}
    >
      <button
        type="button"
        className="tk-filter-chip__toggle"
        aria-pressed={active}
        onClick={onToggle}
      >
        {icon === undefined ? null : <span className="tk-filter-chip__icon">{icon}</span>}
        <span className="tk-filter-chip__label">{label}</span>
        {count === undefined ? null : (
          <>
            {/* Explicit space keeps the accessible name "Kinds 2153"; the
                flex layout ignores the whitespace box. */}{" "}
            <span className="tk-filter-chip__count">{count}</span>
          </>
        )}
      </button>
      {removable ? (
        <button
          type="button"
          className="tk-filter-chip__remove"
          aria-label={`Remove ${label}`}
          onClick={onRemove}
        >
          <X size={12} aria-hidden="true" />
        </button>
      ) : null}
    </span>
  );
}
